import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ObjectTypeInfoColumnType,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoReferenceViewModel,
  ObjectTypeInfoReferencingViewModel,
  ObjectTypeInfoViewModel,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { FilterOption } from '@barthauer/abc/queries/opath/core';
import { _AbcTreeComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { DxScrollViewComponent } from 'devextreme-angular';
import { ItemClickEvent, ItemExpandedEvent } from 'devextreme/ui/tree_view';
import { HeaderButton } from 'libs/abc/queries/opath/core/src/lib/viewModels/layout.viewModel';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbcExpressionsEditorService } from '../../expressions-editor.service';
import { EntityButtonId, SelectedEntity } from '../../models/entity.model';
import { TranslationConfiguration } from '../../models/view.model';
import { SharedService } from '../../services/shared.service';

@Component({
  selector: 'expressions-editor-entity',
  templateUrl: './entity.component.html',
  styleUrls: ['./entity.component.scss'],
  standalone: false,
})
export class EntityComponent implements OnDestroy, AfterViewChecked, OnInit, OnChanges {
  @ViewChild('entityTree') entityTree: _AbcTreeComponent;
  @ViewChild('scroller') scroller: DxScrollViewComponent;
  @Output() onEntitySelected: EventEmitter<SelectedEntity> = new EventEmitter<SelectedEntity>();
  @Output() onColumnsDictionaryUpdated: EventEmitter<void> = new EventEmitter<void>();
  @Output() onPinChange: EventEmitter<void> = new EventEmitter<void>();
  @Input() neutralDesignationActive: boolean;
  @Input() set entityData(data: ObjectTypeInfoViewModel) {
    this.objectTypeInfo = data;
    this.createTreeData(this.objectTypeInfo).then((data) => (this.rootTreeNodes = data));
  }

  private _pinned: boolean = true;
  @Input()
  set pinned(value: boolean) {
    this._pinned = value;
    this.refreshButtonsState();
  }
  get pinned(): boolean {
    return this._pinned;
  }
  public _showPrefernceColumn: boolean = true;

  set showPrefernceColumn(value: boolean) {
    this._showPrefernceColumn = value;
  }
  get showPrefernceColumn(): boolean {
    return this._showPrefernceColumn;
  }
  public _showKeyColumn: boolean = true;

  set showKeyColumn(value: boolean) {
    this._showKeyColumn = value;
  }
  get showKeyColumn(): boolean {
    return this._showKeyColumn;
  }

  public entityButtons: HeaderButton[] = [];

  public currentFilterOption: FilterOption | undefined;
  public rootTreeNodes: _AbcTreeNode[];
  public translation: TranslationConfiguration;
  public searchTerm: string = '';
  public isPrefernce: boolean = true;

  public searchMode: string;
  public columnsDictionary: { key: string; value: string }[] = [];

  private objectTypeInfo: ObjectTypeInfoViewModel;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private EMPTYSTRING: string = '';
  private res: ResourceSet;

  constructor(
    private sharedService: SharedService,
    protected abcExpressionsEditorService: AbcExpressionsEditorService,
    private resourceManagerService: ResourceManagerService
  ) {
    this.translation = this.sharedService.translation;
    this.refreshButtonsState();
  }

  async ngOnInit(): Promise<void> {
    this.abcExpressionsEditorService.nodesTypeFilter.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.rootTreeNodes && this.filterTreeByActiveKeys(this.rootTreeNodes[0].children);
      this.entityTree?.instance?.repaint();
    });
    this.toggleNeutralDesignation();
  }

  ngAfterViewChecked(): void {
    this.entityTree?.searchValueChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.entityTree.instance.collapseAll();
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['neutralDesignationActive']) {
      this.toggleNeutralDesignation();
    }
  }

  ngAfterViewInit(): void {
    this.entityTree?.searchValueChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      setTimeout(() => {
        this.collapseEmptyNodes(this.rootTreeNodes[0]);
      }, 0);
    });
  }

  public pin(): void {
    this.onPinChange.emit();
  }

  /**
   * Collapses a given node if it is empty and all its children if they are too.
   * @param node
   * @returns void
   */
  private collapseEmptyNodes(node: _AbcTreeNode): void {
    for (let leaf of node.children) {
      if (leaf.children.length && !leaf.children[0].visible) {
        this.entityTree.instance.collapseItem(leaf);
      } else {
        this.collapseEmptyNodes(leaf);
      }
    }
  }

  public toggleNeutralDesignation = (): void => {
    this.rootTreeNodes && this.translateNode(this.rootTreeNodes[0]);
  };

  public onItemClick(event: ItemClickEvent): void {
    const treeNode: _AbcTreeNode = event.itemData as _AbcTreeNode;

    const isReferencing = treeNode.data instanceof ObjectTypeInfoReferencingViewModel;

    let parentNode:
      | ObjectTypeInfoColumnViewModel
      | ObjectTypeInfoReferenceViewModel
      | ObjectTypeInfoReferencingViewModel = undefined;

    if (event.event.ctrlKey) {
      parentNode = treeNode.parentNode.data as
        | ObjectTypeInfoColumnViewModel
        | ObjectTypeInfoReferenceViewModel
        | ObjectTypeInfoReferencingViewModel;
    }

    this.onEntitySelected.emit({
      id: treeNode.id,
      mainObjectTypeInfoViewModel: this.objectTypeInfo,
      objectInfo: treeNode.data as
        | ObjectTypeInfoColumnViewModel
        | ObjectTypeInfoReferenceViewModel
        | ObjectTypeInfoReferencingViewModel,
      isCTRLKeyHold: event.event.ctrlKey,
      parentObjectInfo: parentNode,
      isReferencing: isReferencing,
    });
  }

  public async onItemExpanded(event: ItemExpandedEvent): Promise<void> {
    let scrollToNode: boolean = false;
    const treeNode: _AbcTreeNode = event.itemData as _AbcTreeNode;
    scrollToNode = treeNode.children.filter((child) => child.visible === false).length === 1;

    await this.expandTreeItem(treeNode);
    this.filterTreeByActiveKeys(treeNode.children);
    this.recoverScrollPosition(scrollToNode);
  }

  private recoverScrollPosition(scrollToNode: boolean): void {
    let scrollPosition: number = 0;
    scrollPosition = this.scroller.instance.scrollOffset();

    setTimeout(() => {
      scrollToNode ? this.scroller.instance.scrollBy(scrollPosition) : null;
    }, 1);
  }

  public searchTermChanged(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  public filterOptionChanged(filterOption: FilterOption): void {
    this.currentFilterOption = filterOption;
  }

  private async expandTreeItem(treeNode: _AbcTreeNode): Promise<void> {
    if (
      (treeNode.data instanceof ObjectTypeInfoReferenceViewModel ||
        treeNode.data instanceof ObjectTypeInfoReferencingViewModel) &&
      treeNode.children.length === 1
    ) {
      const objectTypeInfo: ObjectTypeInfoViewModel = treeNode.data.objectType;

      treeNode.children = [];
      this.createTreeColumns(objectTypeInfo.columnsByName, treeNode);

      this.createTreeExpandableColumns(objectTypeInfo.referencesByName, treeNode);
      this.createTreeExpandableColumns(objectTypeInfo.referencingByName, treeNode);

      // TODO Kann man evtl. optimieren
      for (const child of treeNode.children) {
        this.entityTree.addNode(child);
      }
    }
  }

  private toggleNodesByTypes(nodeTypes: string[]): void {
    const nodesTypeFilter = this.abcExpressionsEditorService.nodesTypeFilter;

    nodeTypes.forEach((type: string) => {
      if (nodesTypeFilter.value.includes(type)) {
        const index = nodesTypeFilter.value.indexOf(type);
        nodesTypeFilter.value.splice(index, 1);
        nodesTypeFilter.next(nodesTypeFilter.value);
      } else {
        nodesTypeFilter.next([...nodesTypeFilter.value, type]);
      }
    });
  }

  private filterTreeByActiveKeys(treeNodes: _AbcTreeNode[]): void {
    for (let node of treeNodes) {
      if (
        node.icon === this.EMPTYSTRING ||
        (!this.abcExpressionsEditorService.nodesTypeFilter.value.includes(
          node.data ? node.data['columnType'] : undefined
        ) &&
          !node.children.length)
      ) {
        node.visible = false;
      } else {
        node.visible = true;

        this.filterTreeByActiveKeys(node.children);
      }
    }
  }

  private async createTreeData(objectTypeInfo: ObjectTypeInfoViewModel): Promise<_AbcTreeNode[]> {
    this.res = await this.resourceManagerService.getResourceSet('abc-opath-expressions');

    const rootNode = new _AbcTreeNode();
    rootNode.caption = this.neutralDesignationActive ? objectTypeInfo.name : objectTypeInfo.displayText;
    rootNode.data = objectTypeInfo;
    rootNode.id = objectTypeInfo.name;
    rootNode.icon = this.EMPTYSTRING;

    this.createTreeColumns(objectTypeInfo.columnsByName, rootNode);

    this.createTreeExpandableColumns(objectTypeInfo.referencesByName, rootNode);
    this.createTreeExpandableColumns(objectTypeInfo.referencingByName, rootNode);
    setTimeout(() => {
      this.entityTree?.instance?.repaint();
    }, 500);
    return [rootNode];
  }

  private async createTreeColumns(
    columns: Map<string, ObjectTypeInfoColumnViewModel>,
    parentNode: _AbcTreeNode
  ): Promise<void> {
    columns.forEach(async (columnInfo: ObjectTypeInfoColumnViewModel) => {
      await this.createColumn(columnInfo, parentNode);
      this.onColumnsDictionaryUpdated.emit();
    });
  }

  private async createColumn(columnInfo: ObjectTypeInfoColumnViewModel, parentNode: _AbcTreeNode): Promise<void> {
    let treeNode = new _AbcTreeNode();
    treeNode.caption = this.neutralDesignationActive ? columnInfo.name : columnInfo.displayText;
    treeNode.id = parentNode.id + '_' + columnInfo.name;
    treeNode.parentId = parentNode.id;
    treeNode.children = [];
    treeNode.data = columnInfo;

    if (
      columnInfo.columnType === ObjectTypeInfoColumnType.IdRef ||
      columnInfo.columnType === ObjectTypeInfoColumnType.PrimaryKey ||
      columnInfo.columnType === ObjectTypeInfoColumnType.ObjectType
    ) {
      treeNode.icon = this.res.svgName('key-image');
    } else if (columnInfo.columnType === ObjectTypeInfoColumnType.Sys) {
      treeNode.icon = this.res.svgName('sys-image');
    } else {
      treeNode.icon = this.res.svgName('box-image');
    }

    treeNode.parentNode = parentNode;
    parentNode.children.push(treeNode);
    if (!this.columnsDictionary.some((entry) => entry.key === columnInfo.displayText)) {
      this.columnsDictionary.push({ key: columnInfo.displayText, value: columnInfo.name });
    }
  }

  private createTreeExpandableColumns(
    data: Map<string, ObjectTypeInfoReferenceViewModel> | Map<string, ObjectTypeInfoReferencingViewModel>,
    parentNode: _AbcTreeNode
  ): void {
    data.forEach((element: ObjectTypeInfoReferenceViewModel | ObjectTypeInfoReferencingViewModel) => {
      const treeNode = new _AbcTreeNode();
      treeNode.caption = this.neutralDesignationActive ? element.name : element.displayText;
      treeNode.id = parentNode.id + '_' + element.name;
      treeNode.parentId = parentNode.id;
      treeNode.children = [];
      treeNode.data = element;
      treeNode.icon =
        element instanceof ObjectTypeInfoReferenceViewModel
          ? this.res.svgName('referenced-image')
          : element instanceof ObjectTypeInfoReferencingViewModel
          ? this.res.svgName('referencing-image')
          : null;

      const subChildren = new _AbcTreeNode();
      subChildren.icon = this.EMPTYSTRING;
      subChildren.visible = false;

      // critical for tree search purposes !
      subChildren.caption = treeNode.caption;
      treeNode.children.push(subChildren);
      treeNode.parentNode = parentNode;
      parentNode.children.push(treeNode);

      if (!this.columnsDictionary.some((entry) => entry.key === element.displayText)) {
        this.columnsDictionary.push({ key: element.displayText, value: element.name });
        this.onColumnsDictionaryUpdated.emit();
      }
    });
  }

  private translateNode(node: _AbcTreeNode): void {
    if (node.data) {
      node.caption = this.neutralDesignationActive
        ? (node.data as ObjectTypeInfoReferenceViewModel).name
        : (node.data as ObjectTypeInfoReferenceViewModel).displayText;
    }

    if (node.children.length) {
      for (let child of node.children) {
        this.translateNode(child);
      }
    }
  }

  public refreshButtonsState(): void {
    this.entityButtons = [
      {
        id: EntityButtonId.pinnedBtn,
        icon: 'pin',
        toolTipContent: this.translation?.pin,

        statfullButton: true,
        active: this.pinned,
        action: async () => this.pin(),
      },
      {
        id: EntityButtonId.keyBtn,
        icon: 'key',
        toolTipContent: this.translation?.attribute.keyColumns,
        statfullButton: true,
        active: this.showKeyColumn,
        action: async () => {
          this.showKeyColumn = !this.showKeyColumn;
          this.toggleNodesByTypes([
            ObjectTypeInfoColumnType.IdRef,
            ObjectTypeInfoColumnType.PrimaryKey,
            ObjectTypeInfoColumnType.ObjectType,
          ]);
        },
      },
      {
        id: EntityButtonId.preferenceBtn,
        icon: 'preferences',
        toolTipContent: this.translation?.attribute.systemColumns,
        statfullButton: true,
        active: this.showPrefernceColumn,
        action: async () => {
          this.showPrefernceColumn = !this.showPrefernceColumn;
          this.toggleNodesByTypes([ObjectTypeInfoColumnType.Sys]);
        },
      },
    ];
  }
}
